import { nothing } from 'lit';
import { type ColorToken, toCSSValue } from '@loadsmart/miranda-tokens';

import type { PropertyValues, SVGTemplateResult } from 'lit';

import styles from './icon.styles';
import { Component } from '../component';
import AVAILABLE_ICONS, { type IconName } from './available-icons';

export interface IconProps {
	name: IconName;
	size?: string;
	color?: ColorToken;
}

/**
 * This component is meant to be used only inside this repository.
 * The icon's `color` and `size` are intentionally not set by default so that they can be customized via:
 * - setting color and size CSS custom properties
 * - setting color and size attributes
 * Notice that setting color and size through their attributes will override any set CSS custom properties!
 */
export class Icon extends Component {
	static override styles = [styles()];

	static override get properties() {
		return {
			name: { type: String },
			size: { type: String },
			color: { type: String },
			loading: { state: true },
		};
	}

	/**
	 * Icon color.
	 */
	declare color: ColorToken;

	/**
	 * Icon to be rendered.
	 */
	declare name: IconProps['name'];

	/**
	 * Icon size.
	 */
	declare size: IconProps['size'];

	private icon: SVGTemplateResult | null = null;

	constructor() {
		super();

		this.icon = null;
	}

	override connectedCallback() {
		super.connectedCallback();

		this.#loadIcon();
	}

	override update(changedProperties: PropertyValues<Icon>) {
		super.update(changedProperties);

		if (changedProperties.has('name')) {
			this.#loadIcon();
		}
	}

	override updated(changedProperties: PropertyValues<Icon>) {
		super.updated(changedProperties);

		if (changedProperties.has('size') && this.size != null) {
			this.style.setProperty(
				'--m-icon-size',
				`${parseInt(String(this.size), 10) || 16}px`,
			);
		}

		if (changedProperties.has('color') && this.color != null) {
			this.style.setProperty('--m-icon-color', toCSSValue(this.color));
		}
	}

	override render() {
		if (
			this.name == null ||
			!AVAILABLE_ICONS.includes(this.name) ||
			this.icon == null
		) {
			return nothing;
		}

		return this.icon;
	}

	async #loadIcon() {
		try {
			this.icon = null;

			const { default: iconModule } = await import(`./icons/${this.name}.js`);

			this.icon = iconModule;
		} catch {
			// eslint-disable-next-line no-console
			console.error(
				`failed loading icon "${this.name}". Are you sure it exists?`,
			);
		} finally {
			this.requestUpdate();
		}
	}
}

window.customElements.define('m-icon', Icon);

declare global {
	interface HTMLElementTagNameMap {
		'm-icon': Icon;
	}
}
